import * as appProductApi from '@apis/app-products';
import ProductItem from '@components/product/ProductItem';
import { DiscountCoupon } from '@models/discount-coupon';
import { ProductModel, ProductStatus } from '@models/product';
import { ProductCollection, ProductCollectionSearchItem } from '@models/productCollection';
import { useQuery } from '@tanstack/react-query';
import { getCurrentLocation, getDistanceBetweenGpsCoordinates } from '@utils/common';
import { shuffleArray } from '@utils/dataTransform';
import { cdnImageUrl } from '@utils/image';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/ko';
import { Carousel } from '@components/common/Carousel';

// 쿠팡 상품 섹션 컴포넌트
interface CoupangSectionProps {
  section: {
    id: string;
    title: string;
    description: string;
    coupangProducts: {
      linkUrl: string;
      thumbnailImage: string;
      thunbnailAltText: string;
      width: number;
      height: number;
    }[];
  };
}

const CoupangSection: React.FC<CoupangSectionProps> = ({ section }) => {
  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: 'white',
        borderBottom: '1px solid #eaeaea',
      }}>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 'bold',
          lineHeight: '26px',
          letterSpacing: -0.6,
          marginBottom: '8px',
        }}>
        {section.title}
      </div>

      {section.description && (
        <div
          style={{
            fontSize: '14px',
            color: '#666',
            lineHeight: '20px',
            marginBottom: '16px',
          }}>
          {section.description}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '16px',
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: '8px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#ccc transparent',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px',
            minWidth: '100%',
          }}>
          {section.coupangProducts.map((product, index) => (
            <a
              key={index}
              href={product.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
              }}>
              <img
                src={product.thumbnailImage}
                alt={product.thunbnailAltText}
                width={product.width}
                height={product.height}
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

// 쿠폰 컴포넌트
interface CouponOverlayProps {
  coupon: DiscountCoupon;
  yPosition: number;
  containerHeight: number;
  collectionId: string;
}

const CouponOverlay: React.FC<CouponOverlayProps> = ({ coupon, yPosition, containerHeight, collectionId }) => {
  // y좌표 0이 최상단, y좌표 값만큼 아래로 위치하도록 수정
  // 쿠폰이 이미지 높이를 넘지 않도록 제한 (쿠폰 높이 40px 고려)
  const topPosition = Math.max(0, Math.min(yPosition, containerHeight - 40));

  // 해당 컬렉션의 이미지 정보 찾기
  const collectionData = coupon.applicability?.collections?.find((collection) => collection.id === collectionId);
  const images = collectionData?.images;
  const activeImageUrl = images?.active;
  const inactiveImageUrl = images?.inactive;
  const now = new Date();
  const activeStatus = coupon.status === 'active';
  const issuable = moment(coupon.issueEndDate).toDate() > now && (coupon.issuedCount || 0) < coupon.quantity;

  // 이미지가 없으면 컴포넌트를 렌더링하지 않음
  if (!activeImageUrl) {
    return null;
  }

  if (!activeStatus) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: `${topPosition}px`,
        zIndex: 10,
      }}>
      <img
        src={cdnImageUrl(issuable ? activeImageUrl : inactiveImageUrl)}
        alt={coupon.name}
        style={{
          width: '100%',
          height: 'auto',
        }}
      />
    </div>
  );
};

export const ProductCollectionPreview: React.FC<{
  data: ProductCollection;
  coupons?: DiscountCoupon[];
  previewCouponCoordinates?: { [couponId: string]: number };
  onCoverImageHeightChange?: (height: number) => void;
}> = (props) => {
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [selectedSearchItem, setSelectedSearchItem] = useState<ProductCollectionSearchItem>(
    props.data?.searchItems?.[0] || ({} as ProductCollectionSearchItem),
  );
  const [collectionCoupons, setCollectionCoupons] = useState<DiscountCoupon[]>([]);
  const [coverImageHeight, setCoverImageHeight] = useState<number>(600); // 기본값을 더 크게 설정

  const [gpsLocation, setGpsLocation] = useState<{
    err?: number;
    latitude?: number;
    longitude?: number;
  }>({});

  const searchItemCoordinates = selectedSearchItem?.coordinates?.split(',').map((point) => Number(point));
  const currentLocation = searchItemCoordinates
    ? { longitude: searchItemCoordinates[0], latitude: searchItemCoordinates[1] }
    : gpsLocation;

  const productSearchQuery = useQuery({
    queryKey: [`product-collection-search-products`, JSON.stringify(selectedSearchItem)],
    queryFn: () =>
      appProductApi.searchProducts({
        status: 'open',
        page: 0,
        coordinates:
          selectedSearchItem.type === 'coordinates'
            ? selectedSearchItem.coordinates
            : gpsLocation?.longitude
              ? `${gpsLocation?.longitude},${gpsLocation?.latitude}`
              : undefined,
        size: selectedSearchItem.size || 100,
        saleType: selectedSearchItem.saleType,
        keyword: selectedSearchItem.type === 'keyword' ? selectedSearchItem.keyword : undefined,
        sort: selectedSearchItem.sort,
      }),
    enabled: !!(selectedSearchItem?.keyword || selectedSearchItem?.coordinates),
  });

  const data = props.data;

  useEffect(() => {
    let collectionProducts = data?.products || [];
    if (data?.shuffle) {
      collectionProducts = shuffleArray(data?.products || []);
    }
    setProducts(collectionProducts);
  }, [data?.products]);

  useEffect(() => {
    if (data?.searchItems) {
      setSelectedSearchItem(data.searchItems[0]);
    }

    getCurrentLocation().then((location) => {
      setGpsLocation(location);
    });
  }, [data?.searchItems]);

  useEffect(() => {
    if (productSearchQuery.data?.data.contents) {
      setProducts(productSearchQuery.data?.data.contents);
    }
  }, [productSearchQuery.data?.data.contents]);

  useEffect(() => {
    if (props.coupons) {
      setCollectionCoupons(props.coupons);
    }
  }, [props.coupons]);

  return (
    <div>
      <div
        style={{
          position: 'sticky',
          zIndex: 1,
          top: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '56px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #eff1f4',
          fontSize: '17px',
          fontWeight: 'bold',
          color: '#242424',
        }}>
        {data.title}
      </div>
      <div style={{ height: 'calc(100dvh - 100px)', overflowX: 'hidden', overflowY: 'auto', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            position: 'relative',
            width: '100%',
            marginTop: 0,
            backgroundColor: '#ffffff',
          }}>
          <div
            style={{
              justifyContent: 'center',
              position: 'relative',
              alignItems: 'center',
              flex: 1,
            }}>
            {data?.coverImageUrl && (
              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <img
                  style={{
                    width: '100%',
                  }}
                  src={cdnImageUrl(data?.coverImageUrl)}
                  onLoad={(e) => {
                    const imgElement = e.target as HTMLImageElement;
                    const actualHeight = imgElement.offsetHeight;
                    setCoverImageHeight(actualHeight);
                    // 부모 컴포넌트에 높이 정보 전달
                    if (props.onCoverImageHeightChange) {
                      props.onCoverImageHeightChange(actualHeight);
                    }
                  }}
                />
                {/* 쿠폰 오버레이 렌더링 */}
                {collectionCoupons.map((coupon) => {
                  const collectionData = coupon.applicability?.collections?.find(
                    (collection) => collection.id === data._id,
                  );
                  // 실시간 미리보기 좌표가 있으면 우선 사용, 없으면 저장된 좌표 사용
                  const yPosition =
                    props.previewCouponCoordinates?.[coupon._id] !== undefined
                      ? props.previewCouponCoordinates[coupon._id]
                      : collectionData?.coordinates?.y || 0;

                  return (
                    <CouponOverlay
                      key={coupon._id}
                      coupon={coupon}
                      yPosition={yPosition}
                      containerHeight={coverImageHeight}
                      collectionId={data._id}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {data?.showUserContents === true && (data?.userContentIds || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '-8px' }}>
              <div style={{ padding: '0 20px', fontSize: '20px', fontWeight: '700', lineHeight: '26px' }}>
                {data.userContentsTitle || '생생후기'}
              </div>
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '0 20px 4px' }}>
                {(data.userContentIds || []).slice(0, 6).map((userContentId, index) => (
                  <div
                    key={`preview-user-content-${userContentId}-${index}`}
                    style={{
                      flex: '0 0 260px',
                      background: '#F5F7FB',
                      border: '1px solid #E8EAED',
                      borderRadius: '12px',
                      padding: '12px',
                    }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div
                        style={{
                          width: '72px',
                          height: '72px',
                          borderRadius: '8px',
                          background: '#E8EAED',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 700,
                            color: '#242424',
                            marginBottom: '4px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          연결 후기 미리보기
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', lineHeight: '17px' }}>
                          리뷰 카드 노출 형태를 확인하는 프리뷰 영역
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        marginTop: '8px',
                        fontSize: '11px',
                        color: '#9B9DA0',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {userContentId}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data?.detailImages && data.detailImages.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ padding: '0 20px', fontSize: '20px', fontWeight: '700', lineHeight: '26px' }}>상세보기</div>
              {(data.detailImagesLayout ?? 'list') === 'grid' ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '4px',
                    padding: '0 20px',
                    width: '100%',
                  }}>
                  {data.detailImages.map((imageUrl: string, index: number) => (
                    <img
                      key={`collection-detail-image-grid-${index}`}
                      src={cdnImageUrl(imageUrl)}
                      alt=""
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              ) : (
                <Carousel
                  options={{
                    align: 'start',
                    containScroll: 'trimSnaps',
                  }}
                  indicatorType="line"
                  styles={{
                    style: { width: '100%', padding: '0 20px' },
                    lineStyle: { position: 'relative', bottom: 0, marginTop: '16px' },
                  }}>
                  {data.detailImages.map((imageUrl: string, index: number) => (
                    <img
                      key={`collection-detail-image-${index}`}
                      src={cdnImageUrl(imageUrl)}
                      alt=""
                      style={{
                        flex: '0 0 100%',
                        maxWidth: '100%',
                        aspectRatio: '4/5',
                        objectFit: 'cover',
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </Carousel>
              )}
            </div>
          )}
          {data?.precautions && data.precautions.length > 0 && (
            <div
              style={{
                padding: '40px 20px',
                color: '#808387',
                backgroundColor: '#EFF1F4',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}>
              {data.precautions.map((precaution, index) => (
                <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      lineHeight: '20px',
                      color: '#808387',
                    }}>
                    {precaution.title}
                  </div>
                  <div style={{ fontSize: '12px', lineHeight: '17px', color: '#808387' }}>
                    <ul style={{ margin: 0, padding: 0, listStyleType: 'disc', paddingLeft: '18px' }}>
                      {precaution.description.split('\n').map((line, lineIndex) => (
                        <li
                          key={lineIndex}
                          style={{
                            marginBottom: lineIndex < precaution.description.split('\n').length - 1 ? '8px' : '0',
                            marginLeft: '0',
                          }}>
                          <span style={{ lineHeight: '17px' }}>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
          {data?.searchItems && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {data?.searchItems.map((item: ProductCollectionSearchItem) => {
                const selected = selectedSearchItem?.id === item.id;
                return (
                  <button
                    key={item.id}
                    className="link-button"
                    style={{ height: '32px' }}
                    onClick={() => {
                      setSelectedSearchItem(item);
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        height: '32px',
                        borderRadius: '16px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        fontSize: '14px',
                        border: selected ? '1px solid #424242' : '1px solid #e8eaed',
                        color: selected ? '#ffffff' : '#424242',
                        backgroundColor: selected ? '#242424' : '#ffffff',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <div>{item.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {/* 쿠팡 상품 섹션들 */}
        {data?.type === 'coupang-products' && data?.coupangSections && data.coupangSections.length > 0 && (
          <>
            {data.coupangSections.map((section) => (
              <CoupangSection key={section.id} section={section} />
            ))}
            <div
              style={{
                fontSize: '12px',
                color: '#66686b',
                textAlign: 'left',
                lineHeight: '18px',
                letterSpacing: -0.2,
                padding: '16px',
              }}>
              쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.
            </div>
          </>
        )}

        {/* 일반 상품 목록 (쿠팡상품 타입이 아닐 때만 표시) */}
        {data?.type !== 'coupang-products' && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '16px',
            }}>
            {products.map((item, index) => {
              const exposed =
                item.display && (item.status === ProductStatus.OPEN || item.status === ProductStatus.CLOSE);
              if (exposed) {
                const distance =
                  currentLocation?.latitude &&
                  currentLocation?.longitude &&
                  item.gpsLocation?.coordinates &&
                  getDistanceBetweenGpsCoordinates(
                    item.gpsLocation?.coordinates[1],
                    item.gpsLocation?.coordinates[0],
                    currentLocation?.latitude,
                    currentLocation?.longitude,
                  );

                return (
                  <ProductItem
                    distance={distance}
                    key={`product-item-${index}`}
                    item={item}
                    style={{ flex: '0 0 auto', width: 'calc(50% - 6px)' }}
                    onClick={() => {}}></ProductItem>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};
